import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Typography } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';

import LoginLogoDark from '@/assets/login-logo-dark.png';
import LoginLogoLight from '@/assets/login-logo-light.png';
import { AUTH_ERROR_STORAGE_KEY } from '@/constants/common';
import useRemoveAppSkeleton from '@/hooks/useRemoveAppSkeleton';

import useStyles from './Login.styles';
import WaveBackground from './WaveBackground';

const Login = () => {
  const { loginWithRedirect } = useAuth0();
  const { mode: themeMode, setMode: setThemeMode } = useColorScheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email] = useState(() => searchParams.get('email') ?? '');
  const { classes } = useStyles({ themeMode });

  useRemoveAppSkeleton();

  const error = sessionStorage.getItem(AUTH_ERROR_STORAGE_KEY);
  const loginLogo = themeMode === 'dark' ? LoginLogoDark : LoginLogoLight;

  const handleLogin = () => {
    sessionStorage.removeItem(AUTH_ERROR_STORAGE_KEY);
    const authParams = {
      screen_hint: 'login',
      login_hint: email
    };
    loginWithRedirect({ authorizationParams: authParams });
  };

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, {
        variant: 'error',
        preventDuplicate: true,
        onClose: () => {
          sessionStorage.removeItem(AUTH_ERROR_STORAGE_KEY);
        }
      });
    }
  }, [error]);

  useEffect(() => {
    // email already seeded from the query string via initial state
    if (searchParams.get('email')) {
      setSearchParams({});
    }
  }, []);

  // if mui-mode gets deleted out of local storage - override theme being set to 'system' to 'light'
  // this was put here for the MUIv7 upgrade (leaving it here for now)
  useEffect(() => {
    if (themeMode === 'system') {
      setThemeMode('light');
      localStorage.setItem('mui-mode', 'light');
    }
  }, [themeMode]);

  // redirect to dashboard if session exists
  const userSession = localStorage.getItem('access_token');
  if (userSession) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <Box className={classes.login}>
      <WaveBackground themeMode={themeMode} />
      <Box className={classes.wrapper}>
        <Box className={classes.logo}>
          <img src={loginLogo} width={325} alt="Pondurance Scope Logo" />
        </Box>
        <Typography variant="h1" className={classes.title}>
          <span>Hunting Requires Focus.</span> Welcome To Scope.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          size="large"
          onClick={() => handleLogin()}
        >
          SIGN IN
        </Button>
      </Box>
    </Box>
  );
};

export default Login;
